import React, { useMemo } from 'react';

import { Box, type BoxProps } from '@twilio-paste/core/box';
import { DataGridBody, DataGridCell, DataGridRow } from '@twilio-paste/core/data-grid';
import { SkeletonLoader } from '@twilio-paste/core/skeleton-loader';
import type { TdProps } from '@twilio-paste/core/table';

type TableAlignmentOptions = NonNullable<TdProps['textAlign']>;

type TableAlignmentOptionsToContentAlign = Record<
  TableAlignmentOptions,
  BoxProps['justifyContent']
>;

const tableAlignmentOptionsToContentAlign: TableAlignmentOptionsToContentAlign = {
  center: 'center',
  left: 'flex-start',
  right: 'flex-end',
};

interface DataGridCellSkeletonLoaderProps {
  minWidth: number;
  maxWidth: number;
  textAlign?: TableAlignmentOptions;
}

const DataGridCellSkeletonLoader = ({
  minWidth,
  maxWidth,
  textAlign = 'left',
}: DataGridCellSkeletonLoaderProps) => {
  const width = useMemo(
    () => minWidth + Math.random() * (maxWidth - minWidth),
    [minWidth, maxWidth],
  );

  const justifyContent = tableAlignmentOptionsToContentAlign[textAlign] || 'flex-start';

  return (
    <DataGridCell textAlign={textAlign}>
      <Box display="flex" justifyContent={justifyContent}>
        <SkeletonLoader height="1.75rem" width={`${width}px`} />
      </Box>
    </DataGridCell>
  );
};

export interface DataGridBodySkeletonLoaderProps {
  rows: number;
  columns: number;
  widthRanges: number[][];
  textAlign?: TableAlignmentOptions[];
}

export const DataGridBodySkeletonLoader = ({
  rows,
  columns,
  widthRanges,
  textAlign,
}: DataGridBodySkeletonLoaderProps) => {
  const rowItems = Array.from(Array(rows).keys());
  const columnItems = Array.from(Array(columns).keys());

  return (
    <DataGridBody>
      {rowItems.map(row => (
        <DataGridRow key={row}>
          {columnItems.map(column => (
            <DataGridCellSkeletonLoader
              key={column}
              minWidth={widthRanges[column][0]}
              maxWidth={widthRanges[column][1]}
              textAlign={textAlign && textAlign[column]}
            />
          ))}
        </DataGridRow>
      ))}
    </DataGridBody>
  );
};
